import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type { JSX } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Debug from '@/components/Debug';
import NoteList from '@/components/NoteList';
import InPlaceEditor from '@/components/PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import { RecordType } from '@/components/PersonInPlaceEditor/components/InPlaceEditor/types';
import { Field, FieldTypes } from '@/components/PersonInPlaceEditor/types';
import RemitClaimCard from '@/components/RemitClaimCard';
import RemitFinancialInformationCard from '@/components/RemitFinancialInfoCard';
import TaskList from '@/components/TaskList';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { RemitFormPayload } from '@/forms/RemitForm/types';
import useInsurancePlans from '@/hooks/useInsurancePlans';
import RemitClaimRepository from '@/repositories/RemitClaimRepository';
import RemitFinancialInfoRepository from '@/repositories/RemitFinancialInfoRepository';
import RemitRepository from '@/repositories/RemitRepository';
import {
  RemitClaimPayload,
  ClaimStatusRegistry,
  RemitFinancialInfoPayload,
  RemitClaim
} from '@/types/billing';
import { InsurancePlan } from '@/types/insurancePlan';
import Alert from '@/ui/components/Alert';
import Badge from '@/ui/components/Badge';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import Heading from '@/ui/components/Heading';
import PageHeaderBar from '@/ui/components/PageHeaderBar';
import Spinner from '@/ui/components/Spinner';
import { currencyInCentsFormatter } from '@/utils/currencies';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import RemitContext from './contexts/remitContext';
import useRemitQuery from './hooks/useRemitQuery';
import { RemitClaimSchema } from './schemas/RemitClaimSchema';
import { RemitFinancialInformationSchema } from './schemas/RemitFinancialInformationSchema';
import { RemitSchema } from './schemas/RemitSchema';

export const ELECTRONIC_835 = 'electronic_835';

const EditRemit = (): JSX.Element | null => {
  const { addMessage } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightRemitClaimId = searchParams.get('highlight_remit_claim_id');
  const highlightServiceLineId = searchParams.get('highlight_service_line_id');
  const remitRepository = RemitRepository();
  const remitClaimRepo = RemitClaimRepository();
  const remitFinancialInfoRepo = RemitFinancialInfoRepository();

  const { id } = useParams<{ id: string }>();
  const getRemitQuery = useRemitQuery(id || '', true);
  const { data: insurancePlansData } = useInsurancePlans();

  const formatInsurancePlansForOptions = useCallback(async () => {
    if (insurancePlansData?.data)
      // TODO: add a payer ID qualifier to indicate this is a healthie plan name id
      return insurancePlansData.data.map(({ name_and_id, id }) => ({
        label: name_and_id,
        value: id
      }));
    return [];
  }, [insurancePlansData]);

  const keyedInsurancePlans = useMemo(() => {
    return (insurancePlansData?.data || []).reduce(
      (acc, plan) => {
        // TODO: add a payer ID qualifier to indicate this is a healthie plan name id
        acc[plan.id] = plan;
        return acc;
      },
      {} as { [key: string]: InsurancePlan }
    );
  }, [insurancePlansData?.data]);

  const mutateRemit = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Partial<RemitFormPayload> }) => {
      if (id) {
        return remitRepository.updateRemit({ remitId: Number(id), params: values });
      } else {
        return remitRepository.createRemit({
          params: values
        });
      }
    },
    onSuccess: () => {
      addMessage({ level: 'success', text: 'Successfully updated remit' });
      getRemitQuery.refetch();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const mutateRemitFinancialInfo = useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: RemitFinancialInfoPayload }) => {
      if (!id) {
        return remitFinancialInfoRepo.create({ data: values });
      }
      return await remitFinancialInfoRepo.update({ id, data: values });
    },
    onSuccess: async () => {
      await getRemitQuery.refetch();

      addMessage('success', 'Remit Claim saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
  const deleteRemitFinancialInfo = useMutation({
    mutationFn: async (id: string) => {
      await remitFinancialInfoRepo.delete({ id });
    },
    onSuccess: async () => {
      await getRemitQuery.refetch();
      addMessage('success', 'Remit Claim deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
  const mutateRemitClaim = useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: RemitClaimPayload }) => {
      if (!id) {
        return remitClaimRepo.create({ data: values });
      }
      return await remitClaimRepo.update({ data: values });
    },
    onSuccess: async () => {
      await getRemitQuery.refetch();
      addMessage('success', 'Remit Claim saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deleteRemitClaim = useMutation({
    mutationFn: async (id: string) => {
      await remitClaimRepo.delete({ id });
    },
    onSuccess: async () => {
      await getRemitQuery.refetch();
      addMessage('success', 'Remit Claim deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submittedRemitClaimValuesManipulator = async (values: Partial<RemitClaimPayload>) => {
    return values as RemitClaimPayload;
  };

  const submittedRemitValuesManipulator = async (values: Partial<RemitFormPayload>) => {
    return values as RemitFormPayload;
  };

  const submittedRemitFinancialInformationValuesManipulator = async (
    values: Partial<RemitFinancialInfoPayload>
  ) => {
    return values as RemitFinancialInfoPayload;
  };

  const { data, isPending, refetch } = getRemitQuery;
  const remit = data?.data;
  const plansInClaims = useMemo(() => {
    const planNames = new Set(
      remit?.remit_claims?.reduce((acc, claim) => {
        acc.add(
          keyedInsurancePlans[`${claim.financial_info?.payer_identifier}`]?.name_and_id ||
            keyedInsurancePlans[`${claim.financial_info?.other_payer_identifier}`]?.name_and_id ||
            claim.financial_info?.payer_name ||
            'Unknown'
        );
        return acc;
      }, new Set<string>()) || new Set<string>()
    );

    return Array.from(planNames);
  }, [keyedInsurancePlans, remit?.remit_claims]);

  const remitStatusColor = useMemo(() => {
    const statusColorMap = {
      draft: Color.GRAY,
      processed: Color.GREEN,
      parsed: Color.GREEN,
      reconciled: Color.RED,
      failed: Color.RED,
      pending: Color.YELLOW
    };

    if (!remit || !remit?.status) {
      return Color.GRAY;
    }

    const color = statusColorMap[remit?.status as keyof typeof statusColorMap];
    return color;
  }, [remit]);

  if (!id) {
    return null;
  }

  const editable =
    !isPending &&
    !mutateRemitClaim.isPending &&
    !deleteRemitClaim.isPending &&
    !mutateRemitFinancialInfo.isPending &&
    !deleteRemitFinancialInfo.isPending &&
    remit?.type !== ELECTRONIC_835;

  const financialInfoFields: Field<RemitFinancialInfoPayload>[] = [
    {
      key: 'paid_at',
      label: 'Paid At',
      type: FieldTypes.DATE
    },
    {
      key: 'check_amount',
      label: 'Check Amount',
      type: FieldTypes.CURRENCY
    },

    {
      key: 'check_or_eft_trace_number',
      label: 'Check Number',
      type: FieldTypes.TEXT
    },

    {
      key: 'payer_identifier',
      label: 'Payer Identifier',
      type: FieldTypes.SELECT,
      options: formatInsurancePlansForOptions
    },
    {
      key: 'payment_method',
      label: 'Payment Method',
      type: FieldTypes.SELECT,
      options: [
        { value: 'check', label: 'Check' },
        { value: 'eft', label: 'EFT' }
      ]
    },
    {
      alwaysSend: true,
      key: 'remit_id',
      label: 'Remit',
      type: FieldTypes.HIDDEN
    }
  ];

  const remitFields: Field<RemitFormPayload>[] = [
    {
      key: 'status',
      label: 'Status',
      type: FieldTypes.SELECT,
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'processed', label: 'Processed' },
        { value: 'reconciled', label: 'Reconciled' }
      ]
    }
  ];

  const remitClaimsByFinInfo: { [key: string]: RemitClaim[] } | undefined =
    remit?.remit_claims?.reduce(
      (acc, claim) => {
        if (claim.financial_info?.id) {
          const key = claim.financial_info?.id?.toString();
          acc[key] = acc[key] || [];
          acc[key].push(claim);
        }
        return acc;
      },
      remit?.financial_info?.reduce(
        (acc, finInfo) => {
          if (finInfo.id) {
            acc[finInfo.id] = [];
          }
          return acc;
        },
        {} as { [key: string]: RemitClaim[] }
      ) as { [key: string]: RemitClaim[] }
    );

  const claimFields: Field<RemitClaimPayload>[] = [
    {
      alwaysSend: true,
      key: 'person_id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      alwaysSend: true,
      key: 'financial_info_id',
      label: 'Check',
      options:
        remit?.financial_info?.map((fi) => ({
          value: fi.id || '',
          label: `#${fi.check_or_eft_trace_number} for ${currencyInCentsFormatter(fi.check_amount_in_cents || 0)}`
        })) || [],
      type: FieldTypes.SELECT
    },
    {
      key: 'patient_control_number',
      label: 'Patient Control Number',
      type: FieldTypes.TEXT
    },
    {
      alwaysSend: true,
      key: 'healthie_cms_1500',
      label: 'Healthie CMS 1500',
      type: FieldTypes.HIDDEN
    },
    {
      alwaysSend: true,
      key: 'remit_id',
      label: 'Remit',
      type: FieldTypes.HIDDEN
    },
    {
      alwaysSend: true,
      key: 'id',
      label: 'Claim',
      type: FieldTypes.HIDDEN
    },
    // FIXME: [AP-6099] Fix ability to add rules to fields
    // {
    //   key: 'charge_amount',
    //   label: 'Charge Amount',
    //   type: FieldTypes.CURRENCY,
    //   rules: {
    //     disabled: true
    //   }
    // },
    // {
    //   key: 'paid_amount',
    //   label: 'Paid Amount',
    //   type: FieldTypes.CURRENCY,
    //   rules: {
    //     disabled: true
    //   }
    // },
    {
      key: 'payer_classification',
      label: 'Payer Classification',
      type: FieldTypes.SELECT,
      options: [
        { value: 'primary', label: 'Primary' },
        { value: 'secondary', label: 'Secondary' },
        { value: 'tertiary', label: 'Tertiary' },
        { value: 'unspecified', label: 'Unspecified' }
      ]
    },
    {
      key: 'status',
      label: 'Status',
      type: FieldTypes.SELECT,
      options: Object.entries(ClaimStatusRegistry).map(([key, { description }]) => ({
        value: key,
        label: description
      }))
    },
    {
      key: 'payer_claim_control_number',
      label: 'Payer Claim Control Number',
      type: FieldTypes.TEXT,
      rules: {
        required: true
      }
    },
    {
      key: 'claim_received_on',
      label: 'Claim Received On',
      type: FieldTypes.DATE
    },
    {
      key: 'patient_responsibility_amount',
      label: 'Patient Responsibility Amount',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'claim_filing_indicator_code',
      label: 'Claim Filing Indicator Code',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'facility_type_code',
      label: 'Facility Type Code',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'claim_frequency_code',
      label: 'Claim Frequency Code',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'drg_code',
      label: 'DRG Code',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'claim_statement_period_start_on',
      label: 'Claim Statement Period Start On',
      type: FieldTypes.DATE,
      hidden: true
    },
    {
      key: 'claim_statement_period_end_on',
      label: 'Claim Statement Period End On',
      type: FieldTypes.DATE,
      hidden: true
    },
    {
      key: 'claim_coverage_expiration_on',
      label: 'Claim Coverage Expiration On',
      type: FieldTypes.DATE,
      hidden: true
    },
    {
      key: 'claim_contract_code',
      label: 'Claim Contract Code',
      type: FieldTypes.TEXT
    },
    {
      key: 'claim_coverage_amount',
      label: 'Claim Coverage Amount',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'corrected_insured_last_name',
      label: 'Corrected Insured Last Name',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'corrected_insured_first_name',
      label: 'Corrected Insured First Name',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'corrected_insured_middle_name',
      label: 'Corrected Insured Middle Name',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'corrected_insured_prefix',
      label: 'Corrected Insured Prefix',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'corrected_insured_suffix',
      label: 'Corrected Insured Suffix',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'corrected_insured_id_qualifier',
      label: 'Corrected Insured ID Qualifier',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'corrected_insured_id',
      label: 'Corrected Insured ID',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'provider_entity_type_qualifier',
      label: 'Provider Entity Type Qualifier',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'provider_id_qualifier',
      label: 'Provider ID Qualifier',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'provider_id',
      label: 'Provider ID',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'provider_name',
      label: 'Provider Name',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'provider_first_name',
      label: 'Provider First Name',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'provider_middle_name',
      label: 'Provider Middle Name',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'provider_suffix',
      label: 'Provider Suffix',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: 'provider_prefix',
      label: 'Provider Prefix',
      type: FieldTypes.TEXT,
      hidden: true
    },
    {
      key: '_delete',
      label: 'Delete',
      type: FieldTypes.BOOLEAN
    }
  ];

  return (
    <Container>
      <PageHeaderBar>
        <Heading level={1}>Edit Remit</Heading>
      </PageHeaderBar>
      <Container type="page">
        {isPending && (
          <div className="my-3">
            <Spinner></Spinner>
          </div>
        )}

        {!remit && !isPending && (
          <div className="my-3">
            <Alert message={`Remit with id ${id} not found`} level={'default'} />
          </div>
        )}

        {remit && (
          <RemitContext.Provider
            value={{
              remit,
              refetch,
              insurancePlans: keyedInsurancePlans,
              highlightServiceLineId: highlightServiceLineId
            }}>
            <Card className="relative group/subsection">
              <InPlaceEditor
                deleteMutation={deleteRemitFinancialInfo}
                fields={remitFields}
                mutation={mutateRemit}
                record={
                  {
                    ...remit
                  } as RemitFormPayload
                }
                editButtonClass="top-2 right-1"
                recordType={RecordType.Remit}
                submittedValuesManipulator={submittedRemitValuesManipulator}
                schema={RemitSchema}
                subsectionEditor>
                <div>
                  Remit #{remit.id} from <strong>{plansInClaims.filter(Boolean).join(', ')}</strong>
                  <span className="text-gray-500 ml-2">
                    {remit.ingested_at && new Date(remit.ingested_at).toLocaleDateString()}{' '}
                    {remit.ingested_from && `(${remit.ingested_from})`}
                  </span>
                  <Badge className="ml-2" color={remitStatusColor}>
                    {remit.status}
                  </Badge>
                </div>
              </InPlaceEditor>
            </Card>

            <Card className="relative">
              <Heading text="Financial Information" value={3} />

              {(remit?.financial_info || []).map((finInfo) => {
                return (
                  <div key={`${finInfo.id}-fin`} className="group/subsection mb-2 relative">
                    <InPlaceEditor
                      deleteMutation={deleteRemitFinancialInfo}
                      disableEdit={!editable}
                      fields={financialInfoFields}
                      key={finInfo.id}
                      mutation={mutateRemitFinancialInfo}
                      record={
                        {
                          ...finInfo,
                          remit_id: remit?.id
                        } as RemitFinancialInfoPayload
                      }
                      recordType={RecordType.RemitFinancialInfo}
                      schema={RemitFinancialInformationSchema}
                      submittedValuesManipulator={
                        submittedRemitFinancialInformationValuesManipulator
                      }
                      subsectionEditor>
                      <RemitFinancialInformationCard remit={remit} finInfo={finInfo} />
                    </InPlaceEditor>
                  </div>
                );
              })}
              <div className="group/subsection my-2 relative">
                {editable ? (
                  <InPlaceEditor
                    schema={RemitFinancialInformationSchema}
                    subsectionEditor
                    fields={financialInfoFields}
                    mutation={mutateRemitFinancialInfo}
                    newRecord
                    newRecordButtonText="Add Check"
                    record={
                      {
                        remit_id: remit?.id
                      } as RemitFinancialInfoPayload
                    }
                    recordType={RecordType.InsurancePolicy}
                    submittedValuesManipulator={submittedRemitFinancialInformationValuesManipulator}
                  />
                ) : null}
              </div>

              <Debug object={remit as object} />
            </Card>
            <Card className="group/subsection relative mt-3">
              <Heading text="Claims" value={2} />

              {Object.entries(remitClaimsByFinInfo || []).map(
                ([finInfoId, remitClaims]: [string, RemitClaim[]]) => {
                  const financialInfo = remit?.financial_info?.find(
                    (fi) => fi.id === Number(finInfoId)
                  );
                  if (!financialInfo) {
                    return null;
                  }

                  return (
                    <div key={`finInfo-rc-${finInfoId}`}>
                      <RemitFinancialInformationCard remit={remit} finInfo={financialInfo} />
                      {remitClaims.map((remitClaim) => {
                        remitClaim.remit = remit;
                        const highlight = highlightRemitClaimId === String(remitClaim.id);
                        return (
                          <div
                            key={`rc-${remitClaim.id}`}
                            className="group/subsection mb-4 relative">
                            <InPlaceEditor
                              deleteMutation={deleteRemitClaim}
                              doubleClickToEdit={false}
                              disableEdit={!editable}
                              fields={claimFields}
                              key={remitClaim.id}
                              mutation={mutateRemitClaim}
                              record={
                                {
                                  ...remitClaim,
                                  remit_id: remit?.id,
                                  financial_info_id: financialInfo?.id
                                } as RemitClaimPayload
                              }
                              recordType={RecordType.RemitClaim}
                              schema={RemitClaimSchema}
                              submittedValuesManipulator={submittedRemitClaimValuesManipulator}
                              subsectionEditor>
                              <RemitClaimCard
                                claim={remitClaim}
                                highlight={highlight}
                                editable={editable}
                              />
                            </InPlaceEditor>
                          </div>
                        );
                      })}
                      <div className="group/subsection my-2 relative">
                        {editable ? (
                          <InPlaceEditor
                            schema={RemitClaimSchema}
                            subsectionEditor
                            fields={claimFields}
                            mutation={mutateRemitClaim}
                            newRecord
                            newRecordButtonText={`Add Claim to #${financialInfo?.check_or_eft_trace_number}`}
                            record={
                              {
                                remit_id: remit?.id,
                                financial_info_id: financialInfo?.id
                              } as RemitClaimPayload
                            }
                            recordType={RecordType.RemitClaim}
                            submittedValuesManipulator={submittedRemitClaimValuesManipulator}
                          />
                        ) : null}
                      </div>
                    </div>
                  );
                }
              )}
            </Card>

            <NoteList
              object_id={String(remit.polymorphic_id)}
              object_name={`this remit`}
              contentTypeName="remit"
            />
            <TaskList
              object_id={remit.polymorphic_id}
              object_name={`this remit`}
              contentTypeName="billing.remit"
            />

            <button className="text-gray-600" onClick={() => navigate(-1)}>
              Back
            </button>
          </RemitContext.Provider>
        )}
      </Container>
    </Container>
  );
};
export default EditRemit;
